import React, { useState } from "react";

import { IconDefinition, IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faChevronDown,
  faChevronLeft,
} from "@fortawesome/pro-regular-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { Button } from "src/design-system/Button";
import { Dropdown, DropdownItem } from "src/design-system/Dropdown";
import { Icon } from "src/design-system/Icon";
import { Tooltip, TooltipProps } from "src/design-system/Tooltip";

const VARIANTS = {
  default: "bg-indigo-100",
  error: "bg-red-100",
} as const;

const SIZES = {
  default: "py-16",
  compact: "py-4",
} as const;

/**
 * Button styling is intentionally locked to `variant="secondary"` and the
 * default size — these are the only states the Figma EmptyState design
 * specifies. `size`, `variant`, and arbitrary `ReactNode` action slots are
 * deliberately omitted so consumers cannot drift from the design.
 */
export type ActionButtonProps = {
  label: string;
  icon?: IconDefinition;
  isDisabled?: boolean;
  isLoading?: boolean;
  dataLoc?: string;
  tooltip?: Pick<TooltipProps, "title" | "body" | "placement" | "disabled">;
} & (
  | {
      onClick: React.MouseEventHandler<HTMLButtonElement>;
      linkTo?: never;
      openInNewTab?: never;
    }
  | {
      linkTo: string;
      openInNewTab?: boolean;
      onClick?: never;
    }
);

export type ActionMenuLeafItemProps = {
  label: string;
  icon?: IconDefinition;
  dataLoc?: string;
  onClick: () => void;
};

export type ActionMenuGroupItemProps = {
  label: string;
  icon?: IconDefinition;
  dataLoc?: string;
  /** Opens a submenu with a Back row. Leaves only — nesting is capped at one level. */
  items: ActionMenuLeafItemProps[];
  onClick?: never;
};

export type ActionMenuItemProps =
  | ActionMenuLeafItemProps
  | ActionMenuGroupItemProps;

export type ActionMenuProps = {
  label: string;
  icon?: IconDefinition;
  isDisabled?: boolean;
  dataLoc?: string;
  items: ActionMenuItemProps[];
};

export type EmptyProps = {
  dataLoc?: string;
  icon: IconProp;
  headline: string;
  description?: React.ReactNode | undefined;
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
  actionButton?:
    | ActionButtonProps
    | [ActionButtonProps, ActionButtonProps]
    | ActionMenuProps
    | false;
};

const ActionButton: React.FC<ActionButtonProps & { fullWidth?: boolean }> = ({
  label,
  icon,
  isDisabled,
  isLoading,
  dataLoc,
  tooltip,
  fullWidth,
  ...action
}) => {
  const button = (
    <Button
      dataLoc={dataLoc}
      isDisabled={isDisabled}
      fullWidth={fullWidth}
      iconLeft={icon}
      isLoading={isLoading}
      variant="secondary"
      onClick={action.onClick}
    >
      {label}
    </Button>
  );

  const buttonWithTooltip = tooltip ? (
    <Tooltip {...tooltip} asChild>
      {button}
    </Tooltip>
  ) : (
    button
  );

  if (!action.linkTo) {
    return buttonWithTooltip;
  }

  if (action.openInNewTab) {
    return (
      <a href={action.linkTo} rel="noopener noreferrer" target="_blank">
        {buttonWithTooltip}
      </a>
    );
  }

  return <Link to={action.linkTo}>{buttonWithTooltip}</Link>;
};

const ActionMenuItemContent: React.FC<
  Pick<ActionMenuItemProps, "label" | "icon">
> = ({ label, icon }) => (
  <span className="flex items-center gap-2">
    {icon && <Icon icon={icon} size="sm" variant="default" />}
    {label}
  </span>
);

/**
 * Holds the drill-in state. Lives inside `MenuItems`, which unmounts on close,
 * so a reopened menu always starts at the top level.
 */
const ActionMenuPanel: React.FC<
  Pick<ActionMenuProps, "items"> & { dataLoc?: string }
> = ({ items, dataLoc }) => {
  const [openGroup, setOpenGroup] = useState<ActionMenuGroupItemProps | null>(
    null,
  );

  const visibleItems = openGroup ? openGroup.items : items;

  return (
    <>
      {openGroup && (
        <MenuItem
          as={DropdownItem}
          data-loc={`${dataLoc}-back`}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            setOpenGroup(null);
          }}
        >
          <ActionMenuItemContent icon={faChevronLeft} label="Back" />
        </MenuItem>
      )}
      <div className="decideScrollbar max-h-48 overflow-y-auto">
        {visibleItems.map((item, i) => (
          <MenuItem
            key={i}
            as={DropdownItem}
            data-loc={item.dataLoc ?? `${dataLoc}-item-${i}`}
            onClick={(e: React.MouseEvent) => {
              if ("items" in item && item.items) {
                e.preventDefault();
                setOpenGroup(item);
                return;
              }
              item.onClick?.();
            }}
          >
            <ActionMenuItemContent icon={item.icon} label={item.label} />
          </MenuItem>
        ))}
      </div>
    </>
  );
};

const ActionMenu: React.FC<ActionMenuProps> = ({
  label,
  icon,
  isDisabled,
  dataLoc,
  items,
}) => (
  <Menu>
    {/*
     * `Button` destructures a fixed prop list, so `as={Button}` would drop the
     * keyboard handlers and aria props headlessui injects — hence the wrapper.
     */}
    <MenuButton as="div" disabled={isDisabled}>
      <Button
        dataLoc={dataLoc}
        isDisabled={isDisabled}
        iconLeft={icon}
        iconRight={faChevronDown}
        variant="secondary"
      >
        {label}
      </Button>
    </MenuButton>
    <MenuItems anchor={{ to: "bottom", gap: 4 }} as={Dropdown} transition>
      <ActionMenuPanel dataLoc={dataLoc} items={items} />
    </MenuItems>
  </Menu>
);

export const EmptyState: React.FC<EmptyProps> = ({
  dataLoc,
  icon,
  headline,
  description,
  actionButton,
  variant = "default",
  size = "default",
}) => {
  const actionMenu =
    actionButton && !Array.isArray(actionButton) && "items" in actionButton
      ? actionButton
      : undefined;
  const buttons: ActionButtonProps[] = Array.isArray(actionButton)
    ? actionButton
    : actionButton && !("items" in actionButton)
      ? [actionButton]
      : [];
  const hasMultiple = buttons.length > 1;

  return (
    <div
      className={twJoin(
        "flex h-full flex-col items-center justify-center text-center",
        SIZES[size],
      )}
      data-loc={dataLoc}
    >
      <div
        aria-hidden="true"
        className={twJoin(
          "mb-4 inline-block rounded-lg p-3",
          VARIANTS[variant],
        )}
      >
        <Icon
          icon={icon}
          size="md"
          variant={variant === "error" ? "error" : "active"}
        />
      </div>
      <h2
        className="mb-2 inline-block max-w-60 font-inter-semibold-13px text-gray-800"
        data-record="true"
      >
        {headline}
      </h2>
      <p
        className={twJoin(
          "inline-block min-h-0 max-w-60 overflow-hidden break-words font-inter-normal-12px text-gray-500",
          (buttons.length > 0 || !!actionMenu) && "mb-6",
        )}
      >
        {description}
      </p>
      {actionMenu && (
        <ActionMenu
          {...actionMenu}
          dataLoc={actionMenu.dataLoc ?? `${dataLoc}-action`}
        />
      )}
      {buttons.length > 0 && (
        <div
          className={twJoin(
            "flex gap-2",
            hasMultiple && "flex-col items-center",
          )}
        >
          {buttons.map((btn, i) => {
            const button = (
              <ActionButton
                {...btn}
                dataLoc={
                  btn.dataLoc ??
                  (hasMultiple ? `${dataLoc}-action-${i}` : `${dataLoc}-action`)
                }
                fullWidth={hasMultiple}
              />
            );
            return (
              <React.Fragment key={i}>
                {hasMultiple && i > 0 && (
                  <p className="font-inter-medium-12px text-gray-500">or</p>
                )}
                {hasMultiple ? <div className="w-60">{button}</div> : button}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};
